import { useMemo } from "react";
import Flex from "@/shared/components/common/Flex";
import List from "@/shared/components/common/List";
import Typography from "@/shared/components/common/Typography";
import { MapPinSimple, Star } from "@phosphor-icons/react";
import { useDeviceType } from "@/shared/context/DeviceContext";

interface TourCardSummaryProps {
  name: string;
  address: string;
  stars: number;
}

export default function TourCardSummary({
  name,
  address,
  stars,
}: TourCardSummaryProps) {
  const memoizedStars = useMemo(() => {
    return [
      ...new Array(5 - stars).fill({
        active: false,
      }),
      ...new Array(stars).fill({
        active: true,
      }),
    ].map((star, index) => ({ ...star, id: index }));
  }, [stars]);

  return (
    <Flex
      direction={{
        default: "col-reverse",
        md: "row",
      }}
      gap={{
        default: "3",
        md: "0",
      }}
      justifyContent={"between"}
      alignItems={"center"}
      className="w-full"
    >
      <Flex.Item className="w-full md:w-auto">
        <Flex direction={"col"} gap={"2"}>
          {useDeviceType() !== "mobile" && (
            <Typography
              variant="h2"
              fontWeight="semibold"
              className="md:text-lg hidden md:block"
            >
              {name}
            </Typography>
          )}
          <Flex alignItems={"center"} gap={"1"} className="md:pl-6">
            <MapPinSimple
              size={19}
              weight="regular"
              className="text-foreground"
            />
            <Typography
              variant="caption"
              fontWeight="medium"
              className="text-pretty md:text-sm"
            >
              {address}
            </Typography>
          </Flex>
        </Flex>
      </Flex.Item>

      <Flex.Item
        alignSelf={"start"}
        className="w-full md:w-auto md:self-center max-md:pt-2"
      >
        <Flex
          className="w-auto"
          justifyContent={{
            default: "between",
            md: "end",
          }}
        >
          {useDeviceType() === "mobile" && (
            <Typography
              variant="h2"
              fontWeight="semibold"
              className="md:text-lg"
            >
              {name}
            </Typography>
          )}
          <Flex
            gap="1"
            justifyContent="center"
            alignItems="center"
            direction="row-reverse"
          >
            <List
              items={memoizedStars}
              renderItems={({ active }) => (
                <Star
                  size={16}
                  weight="fill"
                  className={
                    active ? "text-secondary" : "text-muted dark:text-muted/10"
                  }
                />
              )}
            />
          </Flex>
        </Flex>
      </Flex.Item>
    </Flex>
  );
}
